import type { ReactNode } from 'react';
import type { DeepKeys, DeepValue } from '@tanstack/react-form';
import { createFormHookContexts } from '@tanstack/react-form';

const contexts = createFormHookContexts();

export const { fieldContext, formContext, useFieldContext, useFormContext } = contexts;

/**
 * Field metadata for validation state tracking.
 */
export type FieldMeta = {
  isTouched: boolean;
  isDirty: boolean;
  isValid: boolean;
  errors: unknown[];
};

/**
 * Typed field API provided to AppField children.
 * This provides type-safe access to field state and methods.
 *
 * Field components (Select, TextField, Checkbox, etc.) are injected by TanStack Form's
 * createFormHook composition and accessible via index signature. Use `field.Select`,
 * `field.TextField`, etc. as needed.
 */
export type TypedFieldApi<TValue> = {
  name: string;
  state: {
    value: TValue;
    meta: FieldMeta;
  };
  handleChange: (value: TValue) => void;
  handleBlur: () => void;
  /** Composed field components from createFormHook (Select, TextField, Checkbox, etc.) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [componentName: string]: any;
};

/**
 * Typed form API returned by useTypedFormContext.
 * This provides type-safe access to form values and methods.
 *
 * Supports nested paths using TanStack Form's DeepKeys/DeepValue utilities.
 * Examples of valid paths:
 * - Top-level: `'name'`, `'email'`
 * - Nested objects: `'user.profile.firstName'`
 * - Arrays: `'items[0].name'`
 * - Dynamic keys: `'settings.${key}.value'` (requires template literal type)
 */
export type TypedFormApi<TFormData> = {
  state: { values: TFormData };
  setFieldValue: <K extends DeepKeys<TFormData>>(field: K, value: DeepValue<TFormData, K>) => void;
  validate: (cause: 'change' | 'blur' | 'mount' | 'submit') => Promise<void>;
  AppField: <K extends DeepKeys<TFormData>>(props: {
    name: K;
    children: (field: TypedFieldApi<DeepValue<TFormData, K>>) => ReactNode;
  }) => ReactNode;
  Subscribe: <TSelected>(props: {
    selector: (state: { values: TFormData; fieldMeta: Record<string, { errors?: unknown[] }> }) => TSelected;
    children: (selected: TSelected) => ReactNode;
  }) => ReactNode;
  AppForm: (props: { children: ReactNode }) => ReactNode;
};

/**
 * Typed version of useFormContext that preserves form data types.
 * Use this when you need type-safe access to form values and methods.
 *
 * @example
 * ```tsx
 * const form = useTypedFormContext<MyFormValues>();
 * form.setFieldValue('myField', 'value'); // type-safe
 *
 * // Nested paths are also supported:
 * form.setFieldValue('user.profile.name', 'John');
 * ```
 */
export function useTypedFormContext<TFormData>() {
  return contexts.useFormContext() as unknown as TypedFormApi<TFormData>;
}
